import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { colorOptions, getRandomColor } from '@/utils/colors';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Upload,
  FileSpreadsheet,
  Image,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle2,
  X,
  TrendingDown,
  TrendingUp,
  Plus,
  Sparkles,
  FileSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  transactionImportApi,
  ParsedTransaction,
  categoryApi,
  ApiCategory,
} from '@/lib/api';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'review' | 'importing' | 'complete';

interface EditableTransaction extends ParsedTransaction {
  id: string;
  selected: boolean;
}

export default function ImportDialog({
  open,
  onOpenChange,
  onImportComplete,
}: ImportDialogProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [transactions, setTransactions] = useState<EditableTransaction[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    count: number;
  } | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [createCategoryDialogOpen, setCreateCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: getRandomColor(),
    type: 'expense' as 'expense' | 'income',
  });
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [pendingTransactionId, setPendingTransactionId] = useState<string | null>(null);

  // Load categories when dialog opens
  const loadCategories = useCallback(async () => {
    try {
      const cats = await categoryApi.getAll();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(
    async (selectedFile: File) => {
      setFile(selectedFile);
      setError(null);
      setAnalyzing(true);

      // Load categories if not loaded
      if (categories.length === 0) {
        await loadCategories();
      }

      try {
        const result = await transactionImportApi.analyzeFile(selectedFile);

        if (result.success && result.transactions.length > 0) {
          // Convert to editable transactions
          const editableTxs: EditableTransaction[] = result.transactions.map(
            (tx, index) => ({
              ...tx,
              id: `tx-${index}`,
              selected: true,
            })
          );
          setTransactions(editableTxs);
          setStep('review');
        } else if (result.success && result.transactions.length === 0) {
          setError(t('import.noTransactionsFound'));
        } else {
          setError(result.error || t('import.analysisError'));
        }
      } catch (err) {
        console.error('Analysis failed:', err);
        setError(
          err instanceof Error ? err.message : t('import.analysisError')
        );
      } finally {
        setAnalyzing(false);
      }
    },
    [categories.length, loadCategories, t]
  );

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect]
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Toggle transaction selection
  const toggleTransaction = (id: string) => {
    setTransactions((prev) =>
      prev.map((tx) =>
        tx.id === id ? { ...tx, selected: !tx.selected } : tx
      )
    );
  };

  // Update transaction category
  const updateCategory = (id: string, categoryId: string) => {
    if (categoryId === '__create_new__') {
      // Open create category dialog with a fresh random color
      setPendingTransactionId(id);
      setCategoryForm({ name: '', color: getRandomColor(), type: 'expense' });
      setCreateCategoryDialogOpen(true);
      return;
    }

    const category = categories.find((c) => c.id === categoryId);
    setTransactions((prev) =>
      prev.map((tx) =>
        tx.id === id
          ? { ...tx, categoryId, categoryName: category?.name }
          : tx
      )
    );
  };

  // Handle create category
  const handleCreateCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error(t('categories.nameRequired'));
      return;
    }

    setCreatingCategory(true);
    try {
      const newCategory = await categoryApi.create(categoryForm);
      toast.success(t('categories.created'));

      // Reload categories
      await loadCategories();

      // If this was triggered from a transaction, update that transaction
      if (pendingTransactionId) {
        updateCategory(pendingTransactionId, newCategory.id);
        setPendingTransactionId(null);
      }

      // Reset form and close dialog
      setCategoryForm({ name: '', color: getRandomColor(), type: 'expense' });
      setCreateCategoryDialogOpen(false);
    } catch (err) {
      console.error('Failed to create category:', err);
      toast.error(t('categories.createError'));
    } finally {
      setCreatingCategory(false);
    }
  };

  // Update transaction field
  const updateTransaction = (
    id: string,
    field: 'date' | 'description' | 'amount',
    value: string | number
  ) => {
    setTransactions((prev) =>
      prev.map((tx) => (tx.id === id ? { ...tx, [field]: value } : tx))
    );
  };

  // Select all transactions
  const selectAll = (selected: boolean) => {
    setTransactions((prev) => prev.map((tx) => ({ ...tx, selected })));
  };

  // Handle import confirmation
  const handleConfirmImport = async () => {
    const selectedTransactions = transactions.filter((tx) => tx.selected);

    if (selectedTransactions.length === 0) {
      toast.error(t('import.noTransactionsSelected'));
      return;
    }

    // Validate all have categories
    const missingCategories = selectedTransactions.filter(
      (tx) => !tx.categoryId
    );
    if (missingCategories.length > 0) {
      toast.error(t('import.missingCategories'));
      return;
    }

    setStep('importing');
    setImporting(true);

    try {
      const result = await transactionImportApi.confirmImport({
        transactions: selectedTransactions.map((tx) => ({
          date: tx.date,
          amount: tx.amount,
          description: tx.description,
          categoryId: tx.categoryId!,
          paymentType: tx.paymentType || 'רגיל',
        })),
        notifyUsers,
      });

      if (result.success) {
        setImportResult({ success: true, count: result.importedCount });
        setStep('complete');
        toast.success(
          t('import.successMessage', { count: result.importedCount })
        );
      } else {
        setError(result.error || t('import.importError'));
        setStep('review');
      }
    } catch (err) {
      console.error('Import failed:', err);
      setError(err instanceof Error ? err.message : t('import.importError'));
      setStep('review');
    } finally {
      setImporting(false);
    }
  };

  // Reset dialog
  const resetDialog = () => {
    setStep('upload');
    setFile(null);
    setTransactions([]);
    setError(null);
    setImportResult(null);
    setNotifyUsers(false);
  };

  // Handle close
  const handleClose = () => {
    if (step === 'complete') {
      onImportComplete();
    }
    resetDialog();
    onOpenChange(false);
  };

  // Get file icon
  const getFileIcon = () => {
    if (!file) return <Upload className="h-12 w-12 text-muted-foreground" />;
    if (file.type.includes('image'))
      return <Image className="h-12 w-12 text-blue-500" />;
    if (file.type.includes('csv') || file.type.includes('text'))
      return <FileText className="h-12 w-12 text-green-500" />;
    return <FileSpreadsheet className="h-12 w-12 text-green-600" />;
  };

  const selectedCount = transactions.filter((tx) => tx.selected).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-[95vw] md:max-w-5xl lg:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            {t('import.title')}
          </DialogTitle>
          <DialogDescription>{t('import.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="space-y-4">
              {/* Drag & Drop Zone */}
              <div
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                  ${dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
                  ${analyzing ? 'pointer-events-none opacity-50' : ''}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.jpg,.jpeg,.png,.webp"
                  onChange={handleInputChange}
                  className="hidden"
                />

                {analyzing ? (
                  <div className="space-y-6 py-4">
                    {/* Animated icons with staggered animation */}
                    <div className="relative flex items-center justify-center h-24">
                      {/* Background glow effect */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-32 h-32 bg-primary/20 rounded-full blur-2xl animate-pulse" />
                      </div>
                      
                      {/* Main animated icons */}
                      <div className="relative flex items-center gap-4">
                        {/* File icon with bounce */}
                        <div className="animate-bounce" style={{ animationDelay: '0ms' }}>
                          <FileSearch className="h-10 w-10 text-primary" />
                        </div>
                        
                        {/* Sparkles with pulse */}
                        <div className="relative">
                          <Sparkles className="h-12 w-12 text-primary animate-pulse" />
                          <Sparkles className="h-6 w-6 text-primary/60 absolute -top-1 -right-1 animate-ping" />
                        </div>
                        
                        {/* AI brain icon with bounce (delayed) */}
                        <div className="animate-bounce" style={{ animationDelay: '150ms' }}>
                          <FileSpreadsheet className="h-10 w-10 text-primary" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Text with animated dots */}
                    <div className="text-center space-y-2">
                      <p className="text-base font-medium text-foreground flex items-center justify-center gap-1">
                        {t('import.analyzing')}
                        <span className="flex gap-0.5">
                          <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('import.analyzingSubtext')}
                      </p>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-full max-w-xs mx-auto">
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full w-full bg-gradient-to-r from-primary/40 via-primary to-primary/40 animate-pulse" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getFileIcon()}
                    <div>
                      <p className="font-medium">{t('import.dropZoneTitle')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('import.dropZoneSubtitle')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      <Badge variant="outline">CSV</Badge>
                      <Badge variant="outline">Excel</Badge>
                      <Badge variant="outline">JPG</Badge>
                      <Badge variant="outline">PNG</Badge>
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t('import.error')}</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Tips */}
              <div className="text-sm text-muted-foreground space-y-1">
                <p className="font-medium">{t('import.tipsTitle')}</p>
                <ul className={`list-disc ${isRTL ? 'mr-4' : 'ml-4'} space-y-0.5`}>
                  <li>{t('import.tip1')}</li>
                  <li>{t('import.tip2')}</li>
                  <li>{t('import.tip3')}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Review Step */}
          {step === 'review' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {t('import.transactionsFound', {
                      count: transactions.length,
                    })}
                  </Badge>
                  <Badge variant="outline">
                    {t('import.selected', { count: selectedCount })}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => selectAll(true)}
                  >
                    {t('import.selectAll')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => selectAll(false)}
                  >
                    {t('import.deselectAll')}
                  </Button>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden max-h-[400px] overflow-auto space-y-4 px-1">
                {transactions.map((tx, index) => (
                  <div
                    key={tx.id}
                    className={`relative rounded-xl shadow-md overflow-hidden transition-all ${
                      !tx.selected
                        ? 'opacity-50 scale-[0.98]'
                        : 'shadow-lg'
                    }`}
                  >
                    {/* Colored accent bar */}
                    <div
                      className={`absolute top-0 bottom-0 w-1.5 ${
                        isRTL ? 'right-0' : 'left-0'
                      } ${tx.amount < 0 ? 'bg-red-500' : 'bg-green-500'}`}
                    />

                    {/* Card content */}
                    <div
                      className={`bg-card border ${
                        isRTL ? 'border-r-0 pr-4' : 'border-l-0 pl-4'
                      } p-4 space-y-4`}
                    >
                      {/* Header: Checkbox + Number + Amount */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={tx.selected}
                            onChange={() => toggleTransaction(tx.id)}
                            className="h-5 w-5 rounded"
                          />
                          <span className="text-xs text-muted-foreground font-medium bg-muted px-2 py-0.5 rounded">
                            #{index + 1}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {tx.amount < 0 ? (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            ) : (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            )}
                            <Badge
                              variant={
                                tx.paymentType === 'תשלומים'
                                  ? 'default'
                                  : tx.paymentType === 'הוראת קבע'
                                    ? 'secondary'
                                    : 'outline'
                              }
                              className="text-xs"
                            >
                              {tx.paymentType === 'תשלומים' &&
                              tx.installmentNumber &&
                              tx.totalInstallments
                                ? `${tx.installmentNumber}/${tx.totalInstallments}`
                                : tx.paymentType || t('import.regular')}
                            </Badge>
                          </div>
                        </div>
                        <div
                          className={`text-xl font-bold ${
                            tx.amount < 0 ? 'text-red-600' : 'text-green-600'
                          }`}
                        >
                          {tx.amount.toLocaleString()}
                        </div>
                      </div>

                      {/* Description - prominent */}
                      <div className="bg-muted/50 rounded-lg p-3">
                        <Label className="text-xs text-muted-foreground mb-1 block">
                          {t('import.description')}
                        </Label>
                        <Input
                          value={tx.description}
                          onChange={(e) =>
                            updateTransaction(tx.id, 'description', e.target.value)
                          }
                          className="h-10 bg-background"
                        />
                      </div>

                      {/* Date + Amount + Category Grid */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            {t('import.date')}
                          </Label>
                          <Input
                            type="date"
                            value={tx.date.split('T')[0]}
                            onChange={(e) =>
                              updateTransaction(tx.id, 'date', e.target.value)
                            }
                            className="h-10"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            {t('import.amount')}
                          </Label>
                          <Input
                            type="number"
                            value={tx.amount}
                            onChange={(e) =>
                              updateTransaction(
                                tx.id,
                                'amount',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="h-10"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            {t('import.category')}
                          </Label>
                          <Select
                            value={tx.categoryId || ''}
                            onValueChange={(value) => updateCategory(tx.id, value)}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue
                                placeholder={t('import.selectCategory')}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                              <Separator className="my-1" />
                              <SelectItem value="__create_new__">
                                <div className="flex items-center gap-2 text-primary font-medium">
                                  <Plus className="h-4 w-4" />
                                  <span>{t('import.createNewCategory')}</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block border rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead className="w-[120px]">
                          {t('import.date')}
                        </TableHead>
                        <TableHead className="min-w-[200px]">
                          {t('import.description')}
                        </TableHead>
                        <TableHead className="text-right w-[90px]">
                          {t('import.amount')}
                        </TableHead>
                        <TableHead className="text-center w-[70px]">
                          {t('import.type')}
                        </TableHead>
                        <TableHead className="text-center w-[100px]">
                          {t('import.paymentType')}
                        </TableHead>
                        <TableHead className="w-[130px]">
                          {t('import.category')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow
                          key={tx.id}
                          className={!tx.selected ? 'opacity-50' : ''}
                        >
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={tx.selected}
                              onChange={() => toggleTransaction(tx.id)}
                              className="h-4 w-4"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="date"
                              value={tx.date.split('T')[0]}
                              onChange={(e) =>
                                updateTransaction(tx.id, 'date', e.target.value)
                              }
                              className="w-[130px] h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={tx.description}
                              onChange={(e) =>
                                updateTransaction(
                                  tx.id,
                                  'description',
                                  e.target.value
                                )
                              }
                              className="h-8 min-w-[180px]"
                              title={tx.description}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={tx.amount}
                              onChange={(e) =>
                                updateTransaction(
                                  tx.id,
                                  'amount',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-[100px] h-8 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            {tx.amount < 0 ? (
                              <div className="flex items-center justify-center gap-1">
                                <TrendingDown className="h-4 w-4 text-red-500" />
                                <span className="text-xs text-red-500 font-medium">
                                  {t('import.expense')}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <TrendingUp className="h-4 w-4 text-green-500" />
                                <span className="text-xs text-green-500 font-medium">
                                  {t('import.income')}
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={
                                tx.paymentType === 'תשלומים'
                                  ? 'default'
                                  : tx.paymentType === 'הוראת קבע'
                                    ? 'secondary'
                                    : 'outline'
                              }
                              className="text-xs whitespace-nowrap"
                            >
                              {tx.paymentType === 'תשלומים' &&
                              tx.installmentNumber &&
                              tx.totalInstallments
                                ? `${tx.paymentType} ${tx.installmentNumber}/${tx.totalInstallments}`
                                : tx.paymentType || t('import.regular')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={tx.categoryId || ''}
                              onValueChange={(value) =>
                                updateCategory(tx.id, value)
                              }
                            >
                              <SelectTrigger className="w-[130px] h-8">
                                <SelectValue
                                  placeholder={t('import.selectCategory')}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                                <Separator className="my-1" />
                                <SelectItem value="__create_new__">
                                  <div className="flex items-center gap-2 text-primary font-medium">
                                    <Plus className="h-4 w-4" />
                                    <span>{t('import.createNewCategory')}</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Separator />

              {/* Options */}
              <div className="flex flex-col gap-4">
                {/* Notify switch */}
                <div className="flex items-center gap-3">
                  <Switch
                    id="notify-users"
                    checked={notifyUsers}
                    onCheckedChange={setNotifyUsers}
                  />
                  <Label
                    htmlFor="notify-users"
                    className="text-sm text-muted-foreground"
                  >
                    {t('import.notifyUsers')}
                  </Label>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={resetDialog}>
                    {t('import.startOver')}
                  </Button>
                  <Button
                    onClick={handleConfirmImport}
                    disabled={selectedCount === 0}
                  >
                    {selectedCount === 1
                      ? t('import.confirmImportOne')
                      : t('import.confirmImport', { count: selectedCount })}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-lg font-medium">{t('import.importing')}</p>
              <p className="text-sm text-muted-foreground">
                {t('import.importingDescription', { count: selectedCount })}
              </p>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && importResult && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
              <p className="text-xl font-semibold">
                {t('import.importComplete')}
              </p>
              <p className="text-muted-foreground">
                {t('import.importedCount', { count: importResult.count })}
              </p>
              <Button onClick={handleClose}>{t('import.close')}</Button>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Create Category Dialog */}
      <Dialog open={createCategoryDialogOpen} onOpenChange={setCreateCategoryDialogOpen}>
        <DialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle>{t('import.createNewCategory')}</DialogTitle>
            <DialogDescription>
              {t('import.createCategoryDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Category Name */}
            <div className="space-y-2">
              <Label htmlFor="category-name">{t('categories.name')}</Label>
              <Input
                id="category-name"
                value={categoryForm.name}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, name: e.target.value })
                }
                placeholder={t('categories.namePlaceholder')}
              />
            </div>

            {/* Category Type */}
            <div className="space-y-2">
              <Label>{t('categories.type')}</Label>
              <RadioGroup
                value={categoryForm.type}
                onValueChange={(value: 'expense' | 'income') =>
                  setCategoryForm({ ...categoryForm, type: value })
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="expense" id="type-expense" />
                  <Label htmlFor="type-expense" className="cursor-pointer">
                    {t('categories.expense')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="income" id="type-income" />
                  <Label htmlFor="type-income" className="cursor-pointer">
                    {t('categories.income')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Category Color */}
            <div className="space-y-2">
              <Label>{t('categories.color')}</Label>
              <div className="grid grid-cols-5 gap-1.5">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      categoryForm.color === color 
                        ? 'border-foreground scale-110 shadow-md' 
                        : 'border-muted-foreground/30 hover:scale-105 hover:border-muted-foreground/50'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setCategoryForm({ ...categoryForm, color })}
                  />
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateCategoryDialogOpen(false);
                  setCategoryForm({ name: '', color: getRandomColor(), type: 'expense' });
                  setPendingTransactionId(null);
                }}
                disabled={creatingCategory}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleCreateCategory}
                disabled={creatingCategory || !categoryForm.name.trim()}
              >
                {creatingCategory ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('common.creating')}
                  </>
                ) : (
                  t('common.create')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

